import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'
import { statusTone } from '@/lib/format'
import type { DealerRole } from '@/types'

export type BadgeTone = 'neutral' | 'green' | 'amber' | 'red' | 'blue' | 'violet' | 'accent'

// Tinted fill plus a hairline of the same hue — legible on black without
// shouting over the one orange accent.
const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-fg/[0.06] text-muted ring-line',
  green: 'bg-positive/10 text-positive ring-positive/30',
  amber: 'bg-caution/10 text-caution ring-caution/30',
  red: 'bg-negative/10 text-negative ring-negative/30',
  blue: 'bg-info/10 text-info ring-info/30',
  violet: 'bg-violet-500/10 text-violet-400 ring-violet-500/30',
  accent: 'bg-accent/10 text-accent ring-accent/30'
}

export interface BadgeProps {
  tone?: BadgeTone
  icon?: LucideIcon
  children: ReactNode
  className?: string
}

export function Badge({ tone = 'neutral', icon: Icon, children, className }: BadgeProps): ReactNode {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-label ring-1 ring-inset',
        TONES[tone],
        className
      )}
    >
      {Icon && <Icon size={11} aria-hidden />}
      {children}
    </span>
  )
}

const STATUS_TONES: Record<string, BadgeTone> = {
  pending: 'neutral',
  trial: 'amber',
  paid: 'green',
  expired: 'red',
  admin: 'violet'
}

/** Paints the server's status string with the tone its meaning deserves. */
export function StatusBadge({ status }: { status: string }): ReactNode {
  return <Badge tone={STATUS_TONES[statusTone(status)] ?? 'neutral'}>{status || '—'}</Badge>
}

const ROLE_LABEL: Record<DealerRole, { label: string; tone: BadgeTone }> = {
  admin: { label: 'Admin', tone: 'violet' },
  primary: { label: 'Primary', tone: 'blue' },
  sub: { label: 'Sub', tone: 'neutral' }
}

export function RoleBadge({ role }: { role: DealerRole }): ReactNode {
  const { label, tone } = ROLE_LABEL[role]
  return <Badge tone={tone}>{label}</Badge>
}
